import EmptyFolder from '@assets/images/common/empty_folder.svg';
import { Reports } from '@core/models/reports/reports.models';
import dayjs from '@utils/dateUtils';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Amplitude } from 'src/amplitude';
import ReportListItem from './ReportListItem';

interface ExportedReportsProps {
  reports: Array<Reports.ReportQueryData>;
  onRetryReport: (report: Reports.ReportQueryData) => void;
}

const ExportedReports: React.FC<ExportedReportsProps> = ({ reports, onRetryReport }) => {
  const { t } = useTranslation();

  const handleDownload = useCallback((fileUrl: string, report: Reports.ReportQueryData) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
    Amplitude.trackDownloadReport(report.type, {
      ...(report.field_count !== undefined && { field_count: report.field_count }),
      ...(report.source !== undefined && { source: report.source }),
      ...(report.max_cloud_coverage !== undefined && {
        max_cloud_coverage: report.max_cloud_coverage,
      }),
      ...(report.date_range_count !== undefined && { date_range_count: report.date_range_count }),
    });
  }, []);

  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => {
      const aTime = a.generated_at ? dayjs(a.generated_at).valueOf() : 0;
      const bTime = b.generated_at ? dayjs(b.generated_at).valueOf() : 0;
      return bTime - aTime;
    });
  }, [reports]);

  return (
    <div className="pt-1">
      <p className="text-xs text-sy-neutral-60 font-semibold uppercase mb-2">
        {t('header.reports.drawer.last_exports')}
      </p>
      {reports?.length > 0 ? (
        <div className="flex flex-col items-start relative w-full">
          {sortedReports.map((report) => (
            <ReportListItem
              key={report.report_id ?? `${report.type}_${report.generated_at}`}
              report={report}
              onRetry={onRetryReport}
              onDownload={handleDownload}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center pt-12">
          <img src={EmptyFolder} alt="No reports" className="w-[164px] h-[90px] mb-6" />
          <p className="text-sm font-semibold mb-1">
            {t('header.reports.drawer.no_recent_reports')}
          </p>
          <p className="text-sm text-sy-neutral-60 max-w-[356px]">
            {t('header.reports.drawer.no_recent_reports_description')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ExportedReports;
